/**
 * Human copy for checkout refusals.
 *
 * The backend surfaces `Error.message` verbatim, which meant a contestant at the
 * Stripe payment sheet could read "Cannot mint intent — payment is in 'cancelled'
 * state". None of those strings answer the only questions that matter on a
 * payment screen: was I charged, is my entry safe, what do I do now?
 *
 * The codes come from the API's structured `code` field (httpClient lifts it onto
 * the error). They are a contract with `api_backend_fastify_server/src/utils/
 * payment-errors.ts` — keep the two in step.
 *
 * Anything unrecognised falls through to the server's own message, so a genuine
 * Stripe decline ("Your card was declined.") still reaches the user unaltered.
 */

const COPY: Record<string, string> = {
  PAYMENT_ALREADY_COMPLETED:
    'This entry is already paid. You have not been charged again — refresh the ' +
    'page to see it confirmed.',
  PAYMENT_IN_FLIGHT:
    'A payment for this entry is already going through. Bank transfers take 3–5 ' +
    'business days to clear. Please do not pay again — refresh the page to check ' +
    'its status.',
  PAYMENT_SESSION_EXPIRED:
    'This checkout has expired. Close this window and choose Pay again to start ' +
    'a fresh one. Nothing was charged.',
  STRIPE_UNAVAILABLE:
    "We couldn't reach our payment provider, so nothing was charged. Please try " +
    'again in a moment.',
};

/** True when the payment can never succeed in this dialog — the user must reopen. */
export function isTerminalPaymentError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return (
    code === 'PAYMENT_ALREADY_COMPLETED' ||
    code === 'PAYMENT_IN_FLIGHT' ||
    code === 'PAYMENT_SESSION_EXPIRED'
  );
}

/**
 * Copy to show the user for a failed checkout call. Prefers our mapped message,
 * then the server's message (Stripe declines are already well worded), then a
 * neutral fallback.
 */
export function paymentErrorCopy(err: unknown, fallback = 'Could not start payment'): string {
  const code = (err as { code?: string } | null)?.code;
  if (code && COPY[code]) return COPY[code];
  const message = err instanceof Error ? err.message : '';
  // Never let an internal refusal leak, even if a new code slips through
  // unmapped — those all begin with "Cannot ".
  if (message && !/^Cannot (mint intent|quote payment|settle|adjust payment)/.test(message)) {
    return message;
  }
  return fallback;
}
